import { useCallback, useEffect, useMemo } from "react";
import Table from "./Table";
import { useTable } from "components/shared/tables/Table/hooks/useTable";
import { useColumns } from "./hooks/useColumns";
import { useGroups } from "features/Settings/General/OrganisationLevelGroups/context";
import { useHistoryDrawer } from "../hooks/drawer/useHistoryDrawer";
import { useNoteModal } from "../hooks/modal/useNoteModal";
import { useInOutDateModal } from "../hooks/modal/useInOutDateModal";
import { useWaiveModal } from "../hooks/modal/useWaiveModal";
import { useCopyModal } from "features/TraxxionTimesheet/components/Timesheet/hooks/modal/useCopyModal";
import { useHasHistoryAccess } from "../drawer/History/Menu/useMenuOptions";
import { useAccess } from "utils/hooks/access";
import { usePreferences } from "features/Settings/Configuration/hooks/usePreferences";
import { useColumnsPreferences } from "features/Settings/Configuration/hooks/useColumnsPreferences";
import { useAddedPayCode } from "features/TimeSheet/components/TimeSheetTable/hooks/useAddedPayCode";
import { useValidations, getActiveColumnKeys } from "../hooks/validation";
import { isDateLocked } from "../helpers/isDateLocked";
import { getJobsBasedOnDate, getPrimaryActiveResource } from "utils/helpers/user";
import { getActiveJobs } from "features/TraxxionTimesheet/helpers/getActiveJobs";
import "./style.scss";

function GroupedTimesheet({
    data: baseData,
    user,
    type,
    isSaving,
    columnVisibility,
    openDeleteModal,
    openSummaryDrawer,
    openOvertimeDrawer,
    onUpdate,
    selectedRows,
    setSelectedRows,
    payGroupItems,
    retroAdjustments,
    scheduledHoursMap,
    dateList,
    refetch,
}: any) {
    const isMyTimesheet = type === "my-timesheet";
    const canViewHistory = useHasHistoryAccess(isMyTimesheet);
    const { hasAccess: canEdit } = useAccess(isMyTimesheet ? "timeSheet.canEditMyTimesheet" : "timeSheet.canEdit");
    const hideRetroAdjustment = usePreferences("timeSheetGeneral.hideRetroAdjustment");
    const { groups } = useGroups();
    const payCode = useAddedPayCode(user);

    const data = useMemo(() => Array.isArray(baseData) ? baseData : [], [baseData]);

    const generateRowForDate = useCallback((date: string, dateRows: any[] = [], isTouched = true) => {
        const jobs = getJobsBasedOnDate(user?.jobHistory || user?.jobs || [], date);
        const activeJobs = getActiveJobs(user, date);
        const job: any = getPrimaryActiveResource(jobs || []);
        let newJob = job ? JSON.parse(JSON.stringify(job)) : {};
        const isJobActive = activeJobs.some((item: any) => item.id === job?.id);
        if (!isJobActive || !newJob?.id) newJob = activeJobs.find((item: any) => item.primary) ?? {};
        const location: any = getPrimaryActiveResource(user?.locations || []);
        const dataIds = new Set(data.map((r: any) => r.id));
        const existing = dateRows.filter((t: any) => !t.id?.startsWith("_added") || dataIds.has(t.id));
        const lastNormal = [...existing].reverse().find(
            (ts: any) => !ts.isNonProductiveTime && ts.hourType !== "unit" && ts.hourType !== "amount"
        );
        const reference = lastNormal ?? existing[existing.length - 1];
      
        const levels = groups?.reduce((total: any, group: any) => {
            const level = group?.level.replace("_", "");
            const value = user?.[level] ?? reference?.[level];
            if (value) return { ...total, [level]: value };
            return total;
        }, {});
        return {
            id: "_added" + crypto.randomUUID(),
            date,
            isAdded: true,
            isTouched,
            user,
            timezone: location?.timezoneValue,
            startDate: reference?.endDate,
            job: newJob,
            location,
            rate: parseFloat(`${newJob?.hourlyRate}`) || 0,
            regRate: parseFloat(`${newJob?.hourlyRate}`) || 0,
            payCode,
            ...levels,
        };
    }, [groups, user, payCode, data]);

    const augmentedData = useMemo(() => {
        const existingDates = new Set(data.map((r: any) => r.date));
        const missingRows = (dateList || [])
            .filter((date: string) => !existingDates.has(date))
            .map((date: string) => generateRowForDate(date, [], false));
        return [...data, ...missingRows];
    }, [data, dateList, generateRowForDate]);

    const isRowDisabled = useCallback((row: any) => {
        const retros = hideRetroAdjustment ? undefined : retroAdjustments?.result;
        return isSaving || !canEdit || !!row?.locked || isDateLocked(row.date, payGroupItems, retros, user?.payGroup);
    }, [isSaving, canEdit, payGroupItems, retroAdjustments, user?.payGroup, hideRetroAdjustment]);

    const isDateDisabled = useCallback((date: string) => {
        const retros = hideRetroAdjustment ? undefined : retroAdjustments?.result;
        return isSaving || !canEdit || isDateLocked(date, payGroupItems, retros, user?.payGroup);
    }, [isSaving, canEdit, payGroupItems, retroAdjustments, user?.payGroup, hideRetroAdjustment]);

    const onUpdateRow = useCallback((id: string, updates: Record<string, any>) => {
        const inBase = data.find((row: any) => row.id === id);
        if (inBase) {
            onUpdate(data.map((row: any) => row.id === id ? { ...row, ...updates, isTouched: true } : row));
        } else {
            const autoRow = augmentedData.find((row: any) => row.id === id);
            if (autoRow) onUpdate([...data, { ...autoRow, ...updates, isTouched: true }]);
        }
    }, [data, augmentedData, onUpdate]);

    const onRemoveRow = useCallback((id: string) => {
        onUpdate(data.filter((row: any) => row.id !== id));
    }, [data, onUpdate]);

    const onAddRow = useCallback((date: string) => {
        const dateRows = augmentedData.filter((r: any) => r.date === date);
        const newRow = generateRowForDate(date, dateRows, true);
        onUpdate([...data, newRow]);
    }, [data, augmentedData, generateRowForDate, onUpdate]);

    const { open: openHistoryDrawer, drawer: historyDrawer } = useHistoryDrawer();
    const { open: openCopyModal, modal: copyModal } = useCopyModal({ refetch });
    const { open: openWaiveModal, modal: waiveModal } = useWaiveModal({ isMyTimesheet, user, refetch });
    const { open: openNoteModal, modal: noteModal } = useNoteModal();
    const { open: openInOutDateModal, modal: inOutDateModal } = useInOutDateModal();

    const baseColumns = useColumns({
        openNoteModal,
        openDeleteModal,
        openHistoryDrawer: canViewHistory ? openHistoryDrawer : undefined,
        openInOutDateModal,
        openOvertimeDrawer,
        isMyTimesheet,
    });

    const feature = useMemo(() => {
        switch (type) {
            case "my-timesheet":
            case "my-weekly-timesheet":
                return "myTimesheet";
            case "weekly":
            case "user-weekly-timesheet":
                return "timeSheetWeekly.timeSheet";
            case "team":
            case "user-timesheet":
            default:
                return "timeSheetTeam.timeSheet";
        }
    }, [type]);

    const hideDailySummary = usePreferences(`${feature}.hideDailySummary`);

    const columns = useColumnsPreferences(baseColumns, `${feature}.columns`);

    const activeColumnKeys = useMemo(() => getActiveColumnKeys(columns), [columns]);
    const { fieldErrors, rowErrors } = useValidations(type, data, activeColumnKeys);

    const meta = useMemo(
        () => ({
            user,
            isMyTimesheet,
            isSaving,
            canEdit,
            isDisabled: isSaving || !canEdit,
            isRowDisabled,
            onUpdateRow,
            onRemoveRow,
            onAddRow,
            selectedRows,
            setSelectedRows,
            openDeleteModal,
            openSummaryDrawer,
            openHistoryDrawer: canViewHistory ? openHistoryDrawer : undefined,
            openCopyModal,
            openWaiveModal,
            scheduledHoursMap,
            refetch,
            errors: fieldErrors,
            isDateDisabled,
            hideDailySummary,
        }),
        [
            user,
            isMyTimesheet,
            isSaving,
            canEdit,
            isRowDisabled,
            isDateDisabled,
            onUpdateRow,
            onRemoveRow,
            onAddRow,
            selectedRows,
            setSelectedRows,
            openDeleteModal,
            openSummaryDrawer,
            canViewHistory,
            openHistoryDrawer,
            openCopyModal,
            openWaiveModal,
            scheduledHoursMap,
            refetch,
            fieldErrors,
            hideDailySummary,
        ],
    );

    const table = useTable({
        data: augmentedData,
        columns,
        totalItems: augmentedData.length,
        enableSorting: false,
        defaultColumnVisibility: columnVisibility,
        meta,
        enableRowSelection: false,
        errors: rowErrors,
    });

    useEffect(() => {
        table.setColumnVisibility(columnVisibility);
    }, [columnVisibility]);

    return (
        <>
            <div className="grouped-table">
                <Table table={table} />
            </div>

            {historyDrawer}
            {copyModal}
            {waiveModal}
            {noteModal}
            {inOutDateModal}
        </>
    );
}

export default GroupedTimesheet;
